// src/cockpit4/components/OfferDesignPanel.tsx
// ============================================
// V1.5 P1-5：Offer 产品设计（需求 → 产品包 → 报价 → 毛利）
//
// 数据流：
//   1. 用户输入客户咨询文本（可从 CRM 客户下拉带入客户名）
//   2. 调用 SKU Runtime design_offer 命令：
//      - 关键词匹配现有 SKU（报价/卖点/适合场景）
//      - 生成产品包条目 + 报价 + 交付成本 + 预计毛利
//      - 落盘 XEAOS/SKU/Offers/{date}.md（Node 环境）
//   3. 「生成报价单」写入 xeaos_quotes（与报价谈判 QuoteNegotiator 共用）
//      → 用户前往「报价谈判」页继续谈判/发送
//
// 原则：无假数据——未命中 SKU 时显示"定制建议"而非伪造产品
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  Loader2,
  Sparkles,
  FileText,
  CheckCircle2,
  RefreshCw,
  Wallet,
  Percent,
  ArrowRight,
  Users,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { SKUCommand, type OfferDesign } from "../../runtime/SKURuntime/types";
import type { CustomerProfile } from "../../runtime/CRMRuntime/types";

// ============================================
// 常量
// ============================================

/** 报价单存储 key（与 QuoteNegotiator 共用） */
const QUOTES_STORAGE_KEY = "xeaos_quotes";
/** 审计日志存储 key（与 AuditLogEnhanced 共用，P1-8 审计活动流） */
const AUDIT_STORAGE_KEY = "xeaos_audit_logs";

/**
 * 追加一条审计记录（AI 动作统一入审计活动流，格式对齐 AuditLogEnhanced）
 * 失败静默：审计写入不阻塞主流程
 */
function appendAudit(opts: {
  action: "create" | "update" | "view";
  module: string;
  target: string;
  detail: string;
  severity: "info" | "warning" | "critical";
}) {
  try {
    const logs = JSON.parse(localStorage.getItem(AUDIT_STORAGE_KEY) || "[]");
    logs.push({
      id: `audit-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      timestamp: new Date().toISOString(),
      userId: "system",
      username: "Hermes",
      action: opts.action,
      module: opts.module,
      target: opts.target,
      detail: opts.detail,
      isSensitive: opts.severity === "critical",
      severity: opts.severity,
    });
    localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify(logs));
  } catch {
    // 静默
  }
}

/** 金额千分位 */
function fmt(v: number): string {
  return v.toLocaleString("zh-CN");
}

export default function OfferDesignPanel() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [customerId, setCustomerId] = useState<string>("");
  const [inquiry, setInquiry] = useState<string>("");
  const [designing, setDesigning] = useState(false);
  const [offer, setOffer] = useState<OfferDesign | null>(null);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [quoteSaved, setQuoteSaved] = useState(false);

  // 加载 CRM 客户（供选择客户名）
  const loadCustomers = useCallback(async () => {
    try {
      if (!RuntimeManager.isInitialized() || !RuntimeManager.hasRuntime(RuntimeName.CRM)) return;
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" } as any);
      const data = resp?.data as any;
      const list: CustomerProfile[] = Array.isArray(data) ? data : Array.isArray(resp) ? resp : [];
      setCustomers(list);
    } catch {
      // 静默：客户列表不可用时仍可手动输入客户名
    }
  }, []);

  useEffect(() => {
    void loadCustomers();
  }, [loadCustomers]);

  // 选中客户 → 自动带入客户名与咨询参考（历史沟通）
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId) || null,
    [customers, customerId]
  );

  // 生成产品包
  const design = useCallback(async () => {
    if (designing) return;
    const text = inquiry.trim();
    if (!text) {
      setMsg({ type: "err", text: "请先输入客户咨询内容（例如：我想用 AI 做酒店营销）" });
      return;
    }
    setDesigning(true);
    setMsg(null);
    setQuoteSaved(false);
    try {
      const resp = await RuntimeManager.execute(RuntimeName.SKU, SKUCommand.DesignOffer, {
        inquiry: text,
        customerName: selectedCustomer?.company || undefined,
      });
      const data = resp?.data as any;
      if (data?.id) {
        setOffer(data as OfferDesign);
        // P1-8：AI 动作写入审计活动流
        appendAudit({
          action: "create",
          module: "SKU/Offer",
          target: `Offer ${data.matchedSkuName ? `（命中 ${data.matchedSkuName}）` : "（定制建议）"}`,
          detail: `客户咨询「${text.slice(0, 40)}…」→ 产品包 ¥${fmt(Number(data.offerPrice))}，毛利率 ${Math.round(Number(data.marginRate) * 100)}%`,
          severity: "info",
        });
        setMsg({
          type: "ok",
          text: `产品包已生成${data.filePath ? "，已落盘 Vault" : "（浏览器模式，未落盘）"}`,
        });
      } else {
        setMsg({ type: "err", text: resp?.error || "未生成产品包，请稍后重试" });
      }
    } catch (e: any) {
      setMsg({ type: "err", text: e?.message || "生成失败" });
    } finally {
      setDesigning(false);
    }
  }, [designing, inquiry, selectedCustomer]);

  // 生成报价单 → 写入 xeaos_quotes（draft 状态），前往报价谈判页继续
  const saveQuote = useCallback(() => {
    if (!offer) return;
    try {
      const now = new Date();
      const existing = JSON.parse(localStorage.getItem(QUOTES_STORAGE_KEY) || "[]");
      const record = {
        id: `quote-${Date.now()}`,
        customerId: selectedCustomer?.id || "",
        customerName: offer.customerName || "未具名客户",
        industry: selectedCustomer?.industry || "未知",
        tier: selectedCustomer?.tier || "B",
        amount: offer.offerPrice,
        status: "draft" as const,
        createdAt: now.toISOString().slice(0, 10),
        validUntil: new Date(now.getTime() + 7 * 86400000).toISOString().slice(0, 10),
        winProbability: Math.min(70, 35 + Math.round(offer.marginRate * 100 / 2)),
        suggestedRange: {
          low: Math.round(offer.offerPrice * 0.9),
          mid: offer.offerPrice,
          high: Math.round(offer.offerPrice * 1.15),
        },
        strategy: offer.solution,
        bottomLine: Math.round(offer.offerPrice * 0.8),
        negotiations: [],
        reviewNote: "",
      };
      existing.push(record);
      localStorage.setItem(QUOTES_STORAGE_KEY, JSON.stringify(existing));
      // P1-8：报价单创建入审计
      appendAudit({
        action: "create",
        module: "CRM/Quote",
        target: `报价单 ¥${fmt(offer.offerPrice)} → ${offer.customerName || "未具名客户"}`,
        detail: `由 Offer 产品包 ${offer.matchedSkuName || "定制建议"} 生成草稿报价单`,
        severity: "info",
      });
      setQuoteSaved(true);
      setMsg({ type: "ok", text: "已生成报价单（草稿）→ 到「报价谈判」页发送给客户" });
    } catch (e: any) {
      setMsg({ type: "err", text: `保存报价单失败：${e?.message || String(e)}` });
    }
  }, [offer, selectedCustomer]);

  const marginPct = offer ? Math.round(offer.marginRate * 100) : 0;
  const marginColor = marginPct >= 60 ? "#34D399" : marginPct >= 30 ? "#FBBF24" : "#F87171";

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-4"
    >
      {/* 顶部装饰光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

      {/* 标题行 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-md flex items-center justify-center bg-purple-500/15 border border-purple-500/30">
            <Package size={13} className="text-purple-400" />
          </span>
          <div>
            <span className="text-[12px] font-semibold text-white tracking-tight">
              Offer 产品设计
            </span>
            <span className="ml-2 text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              Need → Package → Quote → Profit
            </span>
          </div>
        </div>
      </div>

      {/* 提示信息 */}
      {msg && (
        <div
          className={`mb-3 px-3 py-1.5 rounded-md border text-[10px] font-mono ${
            msg.type === "ok"
              ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
              : "border-amber-500/30 bg-amber-500/[0.06] text-amber-300"
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* 输入区 */}
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-2 mb-3">
        {/* 客户选择（可选） */}
        <div className="relative">
          <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-md border border-white/[0.08] bg-white/[0.02]">
            <Users size={11} className="text-blue-400 flex-shrink-0" />
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="flex-1 bg-transparent text-[10px] font-mono text-gray-300 outline-none cursor-pointer appearance-none"
              title="选择 CRM 客户（自动带入客户名）"
            >
              <option value="">无客户（手动命名）</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company || c.contact.name}
                </option>
              ))}
            </select>
          </div>
          {selectedCustomer && (
            <span className="text-[9px] font-mono text-gray-500 mt-0.5 block">
              {selectedCustomer.industry} · {selectedCustomer.tier} 级
            </span>
          )}
        </div>

        {/* 咨询输入 */}
        <input
          value={inquiry}
          onChange={(e) => setInquiry(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void design(); }}
          placeholder="客户咨询，例如：我想用 AI 帮我做酒店营销 / 需要自动生成朋友圈内容…"
          className="px-3 py-2 rounded-md border border-white/[0.08] bg-white/[0.02] text-[11px] font-mono text-gray-200 placeholder:text-gray-600 outline-none focus:border-purple-500/40 transition-colors"
        />

        {/* 生成按钮 */}
        <button
          onClick={() => void design()}
          disabled={designing}
          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
        >
          {designing ? (
            <Loader2 size={12} className="text-purple-300 animate-spin" />
          ) : (
            <Sparkles size={12} className="text-purple-300" />
          )}
          <span className="text-[10px] font-mono text-purple-200 uppercase tracking-wider">
            {designing ? "设计中..." : "设计产品包"}
          </span>
        </button>
      </div>

      {/* 结果区 */}
      <AnimatePresence mode="wait">
        {!offer ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="rounded-xl border border-dashed border-white/[0.08] px-4 py-6 text-center"
          >
            <div className="text-[11px] font-mono text-gray-500">
              输入客户咨询后，系统把它变成「可销售的产品包」——需求 / 解决方案 / 报价 / 毛利一目了然
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={offer.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2.5"
          >
            {/* 报价与毛利总览 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
                <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Wallet size={9} className="text-purple-400" /> 报价
                </div>
                <div className="text-[16px] font-bold text-white">¥{fmt(offer.offerPrice)}</div>
                {offer.matchedSkuName && (
                  <div className="text-[9px] font-mono text-purple-300 mt-0.5">命中：{offer.matchedSkuName}</div>
                )}
              </div>
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
                <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <FileText size={9} className="text-blue-400" /> 交付成本
                </div>
                <div className="text-[16px] font-bold text-white">¥{fmt(offer.deliveryCost)}</div>
                <div className="text-[9px] font-mono text-gray-500 mt-0.5">约 30%</div>
              </div>
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
                <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <CheckCircle2 size={9} style={{ color: marginColor }} /> 预计毛利
                </div>
                <div className="text-[16px] font-bold" style={{ color: marginColor }}>
                  ¥{fmt(offer.grossProfit)}
                </div>
                <div className="text-[9px] font-mono text-gray-500 mt-0.5">报价 − 成本</div>
              </div>
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
                <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Percent size={9} style={{ color: marginColor }} /> 毛利率
                </div>
                <div className="text-[16px] font-bold" style={{ color: marginColor }}>
                  {marginPct}%
                </div>
                <div className="text-[9px] font-mono text-gray-500 mt-0.5">
                  {marginPct >= 60 ? "高毛利，可加投" : marginPct >= 30 ? "健康区间" : "需优化成本"}
                </div>
              </div>
            </div>

            {/* 需求 / 方案 / 产品包 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="text-[10px] font-semibold text-white mb-1.5">客户需求</div>
                <p className="text-[10px] font-mono text-gray-400 leading-relaxed">{offer.need}</p>
                <div className="text-[10px] font-semibold text-white mt-3 mb-1.5">解决方案</div>
                <p className="text-[10px] font-mono text-gray-400 leading-relaxed">{offer.solution}</p>
              </div>
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="text-[10px] font-semibold text-white mb-1.5">产品包内容</div>
                <div className="flex flex-wrap gap-1.5">
                  {offer.packageItems.map((item, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.03] text-[10px] font-mono text-gray-300"
                    >
                      {item.name} × {item.count} {item.unit}
                    </span>
                  ))}
                </div>
                {offer.filePath && (
                  <div className="text-[9px] font-mono text-emerald-400/70 mt-2">
                    已落盘：{offer.filePath}
                  </div>
                )}
              </div>
            </div>

            {/* 操作 */}
            <div className="flex items-center gap-2">
              <button
                onClick={saveQuote}
                disabled={quoteSaved}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
              >
                <FileText size={11} className="text-emerald-300" />
                <span className="text-[10px] font-mono text-emerald-200 uppercase tracking-wider">
                  {quoteSaved ? "报价单已生成" : "生成报价单"}
                </span>
                {quoteSaved && <ArrowRight size={11} className="text-emerald-300" />}
              </button>
              <button
                onClick={() => void design()}
                disabled={designing}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
                title="基于当前咨询重新生成"
              >
                <RefreshCw size={11} className="text-gray-400" />
                <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">再设计</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}
